import { Injectable } from '@angular/core';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { Trip } from 'src/app/data-models/trip-models/trip.model';
import { SharedMethodsService } from '../shared-methods.service';
import { EmailService } from './email.service';
import { UserStorageService } from '../storage/user-storage.service';

@Injectable({
  providedIn: 'root'
})
export class SubmissionService {

  reportsSent: number = 0; // used to keep track of the number of successfully submitted reports when reSendReports() is called

  constructor(
    private onlineStatusService: OnlineStatusService,
    private emailService: EmailService,
    private sharedMethodsService: SharedMethodsService,
  ) { 

  }

  // displays the appropriate success or error message to the user after reSendReports() is finished
  async displayMessage(numUnsubmittedReports: number): Promise<boolean> {

    if (this.reportsSent === numUnsubmittedReports) {
      this.sharedMethodsService.toastReportsSubmitted();
    } else if (!this.reportsSent) {
      this.sharedMethodsService.toastSubmitAnyReportsFailed();
    } else if (this.reportsSent < numUnsubmittedReports) {
      this.sharedMethodsService.toastSubmitAllReportsFailed();
    }

    await SharedMethodsService.delay(2000); // sleep for 2 seconds, so the user sees the message before the page reloads
    return true;

  }

  // attempts to re-send the provided report to DFO, then updates the appropriate arrays and flags
  async updateData(report: any, trip: Trip, updatedTrips: Trip[], isStart: boolean = false): Promise<boolean> {

    let success: boolean = await this.sendReportData(UserStorageService.currUser.userEmail, report.reportID, trip.tripID); // !!!!!
    if (success) { // If the email was sent successfully, update isSubmitted flag and trip array
      this.reportsSent++;
      report.isSubmitted = true;
      report.submissionDate = new Date();
      report.confirmationNumber = "Awaiting response";
      updatedTrips.push(this.updateTrip(report, trip));
      if (isStart)
        this.sharedMethodsService.saveToLookupDictionary(UserStorageService.currUser.userEmail, trip.tripID).subscribe(() => console.log('Saved username to trip lookup database'));
    }

    return true;

  }

  // attempts to re-send all of the user's unsubmitted reports to DFO
  async reSendReports(unsubmittedTrips: Trip[], numUnsubmittedReports: number): Promise<Trip[]> {

    this.reportsSent = 0;
    let updatedTrips: Trip[] = [];

    // the backend composes report emails from the Cloudant copy of the trip, so trips
    // saved while offline (or while sync was failing) must be pushed up before resending
    if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE && !UserStorageService.adminUser)
      await UserStorageService.pushTripData();

      for (let trip of unsubmittedTrips) {

        if (this.isReportUnsubmitted(trip, trip.startFishingReport)) {
          await this.updateData(trip.startFishingReport, trip, updatedTrips, true);
        }

        if (this.isReportUnsubmitted(trip, trip.endFishingReport)) {
          await this.updateData(trip.endFishingReport, trip, updatedTrips);
        }

        if (this.isReportUnsubmitted(trip, trip.cancelFishingReport)) {
          await this.updateData(trip.cancelFishingReport, trip, updatedTrips);
        }

        for (let i: number = 0; i < trip.pauseFishingReports.length; i++) {
          if (this.isReportUnsubmitted(trip, trip.pauseFishingReports[i])) {
            await this.updateData(trip.pauseFishingReports[i], trip, updatedTrips);
          }
        }

        for (let i: number = 0; i < trip.dailyCatchReports.length; i++) {
          if (this.isReportUnsubmitted(trip, trip.dailyCatchReports[i])) {
            await this.updateData(trip.dailyCatchReports[i], trip, updatedTrips);
          }
        }

        for (let i: number = 0; i < trip.offloadReports.length; i++) {
          if (this.isReportUnsubmitted(trip, trip.offloadReports[i])) {
            await this.updateData(trip.offloadReports[i], trip, updatedTrips);
          }
        }

     }    

     await this.displayMessage(numUnsubmittedReports);
     return updatedTrips;

  }

  // returns a new trip with an updated unsubmittedReports array (with the provided report removed)
  updateTrip(report: any, trip: Trip): Trip { 

    let updatedTrip: Trip = trip;

    let index: number = 0;
    for (let date of updatedTrip.unsubmittedReports) {
      if (new Date(date).getTime() === report.completionDate.getTime()) {
        updatedTrip.unsubmittedReports.splice(index, 1);
      }
      index++;
    }

    return updatedTrip;

  }

  // determines if the provided report has not yet been submitted to DFO
  isReportUnsubmitted(trip: Trip, report: any): boolean {

    for (let date of trip.unsubmittedReports) {
      if (new Date(date).getTime() === report.completionDate.getTime()) {
        return true;
      }
    }

    return false;

  }

  // attempts to send an email with the provided report data to DFO, returns true if successful
  sendReportData(username: string, reportID: string, tripID: string): Promise<boolean> {

    return new Promise<boolean>((res) => {

      if (this.onlineStatusService.getStatus() === OnlineStatusType.ONLINE && !UserStorageService.adminUser) { // only attempt to send the email if the user is online
        this.emailService.sendEmail(username, reportID, tripID).then((success: boolean) => {
          res(success); // return the outcome of sendEmail() (true if successful, false otherwise)
        });
      } else { // if the user is offline or sendgrid fails to send, return false
        res(false);
      }
    });

  }

}
